import React from 'react';
import { ChevronRight, CheckCircle2, Clock, XCircle } from 'lucide-react';
import { format } from 'date-fns';
import { directTriggerBookingStatus } from '../../lib/automations/directTrigger';
import { useState } from 'react';

// Debug flag to control logging - set to false to disable logs
const enableStatusBarDebug = false;

const statuses = [
  { value: 'created', label: 'Created' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' }
];

interface BookingStatusBarProps {
  status: string;
  createdAt: string;
  completedAt?: string | null;
  onStatusChange?: (status: string) => void;
  onCompletedAtChange?: (completedAt: string) => void;
  readOnly?: boolean;
  bookingId?: string;
  businessId?: string;
  canEdit?: boolean;
}

export default function BookingStatusBar({ 
  status, 
  createdAt, 
  completedAt, 
  onStatusChange,
  onCompletedAtChange,
  readOnly = false,
  bookingId,
  businessId,
  canEdit = true
}: BookingStatusBarProps) {
  const [isEditingTimestamp, setIsEditingTimestamp] = useState(false);
  const [editTimestamp, setEditTimestamp] = useState('');

  const getStatusIndex = (status: string) => {
    return statuses.findIndex(s => s.value === status);
  };

  const currentIndex = getStatusIndex(status);
  
  // Handle status change and directly trigger automations
  const handleStatusChange = async (newStatus: string) => {
    // Only certain statuses should trigger automations
    const shouldTriggerAutomation = newStatus === 'in_progress' || newStatus === 'completed';
    
    if (enableStatusBarDebug) {
      console.log(`[STATUS BAR] 🔍 Status change: ${status} → ${newStatus}`);
      console.log(`[STATUS BAR] 🔍 Should trigger automation: ${shouldTriggerAutomation}`);
      console.log(`[STATUS BAR] 🔍 Booking ID available: ${!!bookingId}`);
      console.log(`[STATUS BAR] 🔍 Business ID available: ${!!businessId}`);
    }
    
    // First call the parent's onStatusChange to update the database
    if (onStatusChange) {
      onStatusChange(newStatus);
      
      // If it's a status that should trigger automations and we have the booking/business ID
      if (shouldTriggerAutomation && bookingId && businessId) {
        if (enableStatusBarDebug) {
          console.log(`[STATUS BAR] 🚀 Directly triggering automation for status: ${newStatus}`);
          console.log(`[STATUS BAR] 📋 Booking ID: ${bookingId}`);
          console.log(`[STATUS BAR] 📋 Business ID: ${businessId}`);
        }
        
        try {
          const result = await directTriggerBookingStatus(bookingId, newStatus, businessId);
          
          if (result.success) {
            if (enableStatusBarDebug) {
              console.log(`[STATUS BAR] ✅ Successfully triggered ${result.results?.length || 0} actions`);
              // Log each action result for debugging
              if (result.results && result.results.length > 0) {
                result.results.forEach((actionResult, index) => {
                  console.log(`[STATUS BAR] 📋 Action ${index + 1}: Type=${actionResult.type}, Success=${actionResult.success}`);
                  if (!actionResult.success && actionResult.error) {
                    console.error(`[STATUS BAR] ❌ Action error:`, actionResult.error);
                  }
                });
              } else {
                console.log(`[STATUS BAR] ⚠️ No actions were triggered. Check if automations are configured correctly.`);
              }
            }
          } else {
            console.error(`[STATUS BAR] ❌ Failed to trigger automations:`, result.error);
          }
        } catch (error) {
          console.error(`[STATUS BAR] ❌ Error triggering automation:`, error);
        }
      }
    }
  };

  // Format timestamp for datetime-local input
  const formatTimestampForInput = (timestamp: string) => {
    const date = new Date(timestamp);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Handle starting timestamp edit
  const handleEditTimestamp = () => {
    if (completedAt && status === 'completed' && !readOnly && canEdit && onCompletedAtChange) {
      setEditTimestamp(formatTimestampForInput(completedAt));
      setIsEditingTimestamp(true);
    }
  };

  // Handle saving timestamp changes
  const handleSaveTimestamp = () => {
    if (editTimestamp && onCompletedAtChange) {
      const newCompletedAt = new Date(editTimestamp).toISOString();
      onCompletedAtChange(newCompletedAt);
      setIsEditingTimestamp(false);
      setEditTimestamp('');
    }
  };

  // Handle canceling timestamp edit
  const handleCancelTimestamp = () => {
    setIsEditingTimestamp(false);
    setEditTimestamp('');
  };

  // Don't show the status bar if the booking is cancelled
  if (status === 'cancelled') {
    return (
      <div className="bg-[#2f2f2f] text-gray-400 border-b border-[#3f3f3f] overflow-hidden">
        <div className="max-w-5xl mx-auto relative">
          <div className="flex items-center justify-between py-1.5 sm:py-2 px-3 sm:px-6">
            <div className="flex items-center space-x-2">
              <XCircle className="h-4 w-4 text-red-400" />
              <span className="text-red-400 text-sm">Booking Cancelled</span>
            </div>
            <div className="text-xs">
              Cancelled on {format(new Date(completedAt || createdAt), 'MMM d, yyyy')}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#2f2f2f] text-gray-400 border-b border-[#3f3f3f] overflow-x-auto scrollbar-hide">
      <div className="max-w-5xl mx-auto relative">
        <div className="flex items-center justify-between py-2 px-3 sm:px-6 min-w-[500px]">
          <div className="flex items-center space-x-1 sm:space-x-2 text-[10px] sm:text-xs">
            {statuses.map((s, index) => (
              <React.Fragment key={s.value}>
                <button
                  onClick={() => {
                    if (!readOnly && canEdit && index <= currentIndex + 1) {
                      handleStatusChange(s.value);
                    }
                  }}
                  disabled={readOnly || !canEdit || index > currentIndex + 1}
                  className={`font-medium transition-colors px-2 py-1 ${
                    index <= currentIndex
                      ? 'text-white'
                      : index === currentIndex + 1 && !readOnly && canEdit
                      ? 'text-gray-400 hover:text-gray-300'
                      : 'text-gray-600'
                  }`}
                  style={{ touchAction: 'manipulation' }}
                >
                  {s.label}
                </button>
                {index < statuses.length - 1 && (
                  <ChevronRight className={`h-3 w-3 ${
                    index < currentIndex ? 'text-white' : 'text-gray-600'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
          <div className="flex items-center space-x-2 text-[10px] sm:text-xs whitespace-nowrap ml-4">
            {status === 'completed' ? (
              <>
                <CheckCircle2 className="h-3 w-3 text-green-400" />
                {isEditingTimestamp ? (
                  <div className="flex items-center space-x-2">
                    <input
                      type="datetime-local"
                      value={editTimestamp}
                      onChange={(e) => setEditTimestamp(e.target.value)}
                      className="text-xs px-2 py-1 rounded border border-gray-300 bg-white text-gray-900"
                    />
                    <button
                      onClick={handleSaveTimestamp}
                      className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      Save
                    </button>
                    <button
                      onClick={handleCancelTimestamp}
                      className="px-2 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <span 
                    onClick={handleEditTimestamp}
                    className={`${!readOnly && canEdit && onCompletedAtChange ? 'cursor-pointer hover:text-blue-400 hover:underline' : ''}`}
                    title={!readOnly && canEdit && onCompletedAtChange ? 'Click to edit completion time' : ''}
                  >
                    Completed on {completedAt ? format(new Date(completedAt), 'MMM d, yyyy') : 'Unknown'}
                  </span>
                )}
              </>
            ) : (
              <>
                <Clock className="h-3 w-3" />
                <span>Created on {format(new Date(createdAt), 'MMM d, yyyy')}</span>
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Add scrollbar hiding styles */}
      <style jsx global>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;  /* IE and Edge */
          scrollbar-width: none;  /* Firefox */
        }
      `}</style>
    </div>
  );
}